import { select, takeLatest } from 'redux-saga/effects';
import { RootState } from '../..';
import { formatSelectedFilter } from '../../../modules/crm/inPatient/service/crmUtils';
import { COMMON_ERROR, INPATIENT_DATA } from '../../../shared/constants/commonConstants';
import { GLOBAL_URL } from '../../../shared/constants/dataServiceUrl';
import SendRequest from '../../../shared/services/sendRequest.service';
import { ApplicationDetailModel } from '../../models/applicationDetailModel';
import { formatGrievanceDTO } from '../../../utils/utility';
import actionTypes from '../../types/grievanceTypes';

function* getGrievanceListSaga({ payload: onSuccess }: any): Generator {
  const url = `${GLOBAL_URL.CRM_BASE_URL}Provider/Grievance/TicketList`;
  try {
    const { ticketList, isSuccess }: any = yield SendRequest.instance.post(url, {});
    if (isSuccess) {
      onSuccess(ticketList);
    } else {
      onSuccess([]);
    }
  } catch (e) {
    onSuccess([]);
  }
}
function* setGrievanceResponseSaga({ payload: { formData, onSuccess, onError } }: any): Generator {
  const { emailIdPayer, emailIdCC, subject } = formData;
  const sendRequest = SendRequest.instance;
  try {
    const request = {
      emailIdPayer, emailIdCC, subject,
    };

    const { isSuccess, errors }: any = yield sendRequest.post('', request);
    if (isSuccess) {
      onSuccess();
    } else {
      onError(errors.join('/n'));
    }
  } catch (e) {
    onError(COMMON_ERROR);
  }
}
function* fetchClaimList({ payload }: any): Generator {
  const { getClaimsList } = payload;
  const sendRequest = SendRequest.instance;
  const appDetail: any = yield select((state: RootState): ApplicationDetailModel => state.applicationDetail);
  const { filters, globalSearch: searchedValue, filterAction, sortingKey, pageIndex, isNextStatus, nextStatusFilters }: any =
    yield select((state: RootState): any => state.selectedInPatientDetail);
  let request: any = {
    payerId: 99999,
    providerId: appDetail.getProviderId(),
    range: 10,
    startIndex: (pageIndex !== undefined && pageIndex !== null) ? pageIndex : 1,
    isGrievanceSearchEnabled: false,
    fetchGrievanceData: true,
  };

  const [searchKeyword, searchKey] = String(searchedValue).split('$$');
  if (searchKeyword && searchKey) {
    request[searchKey] = searchKeyword;
  }

  if (filterAction !== 'All' && !isNextStatus) {
    request.action = filterAction;
  } else {
    delete request.action;
  }
  if (isNextStatus) {
    request.nextStatus = nextStatusFilters;
  } else {
    delete request.nextStatus;
  }

  const selectedFilters = formatSelectedFilter(filters);
  if (selectedFilters?.length > 0) {
    request = {
      ...request,
      filters: selectedFilters,
    };
  }

  request.isSearchEnabled = (searchKeyword && searchKey) || selectedFilters?.length > 0 ? true : false;

  if (sortingKey) {
    request = {
      ...request,
      sortingKey,
    };
  }

  try {
    const response: any = yield sendRequest.post(`${GLOBAL_URL.CRM_BASE_URL}${INPATIENT_DATA}`, request,
      { isShowErrorInInterceptor: false });
    getClaimsList(response);
  } catch (e) {
    getClaimsList({ isSuccess: false, errors: [COMMON_ERROR] });
  }
}

function* fetchGrievanceListSaga({ payload }: any): Generator {
  const { getClaimsList } = payload;
  const sendRequest = SendRequest.instance;
  const appDetail: any = yield select((state: RootState): ApplicationDetailModel => state.applicationDetail);
  const { filters, globalSearch: searchedValue, filterAction, sortingKey, pageIndex, isNextStatus, nextStatusFilters }: any =
    yield select((state: RootState): any => state.selectedInPatientDetail);
  let request: any = {
    providerId: appDetail.getProviderId(),
    range: 10,
    startIndex: (pageIndex !== undefined && pageIndex !== null) ? pageIndex : 1,
  };

  const [searchKeyword, searchKey] = String(searchedValue).split('$$');
  if (searchKeyword && searchKey) {
    request[searchKey] = searchKeyword;
  }

  if (filterAction !== 'All' && !isNextStatus) {
    request.action = filterAction;
  } else {
    delete request.action;
  }
  if (isNextStatus) {
    request.nextStatus = nextStatusFilters;
  } else {
    delete request.nextStatus;
  }

  const selectedFilters = formatSelectedFilter(filters);
  if (selectedFilters?.length > 0) {
    request = {
      ...request,
      filters: selectedFilters,
    };
  }

  request.isSearchEnabled = (searchKeyword && searchKey) || selectedFilters?.length > 0 ? true : false;

  if (sortingKey) {
    request = {
      ...request,
      sortingKey,
    };
  }

  try {
    const response: any = yield sendRequest.post(`${GLOBAL_URL.CRM_BASE_URL}Provider/Grievance/GetGrievanceByActions`, request,
      { isShowErrorInInterceptor: false });
    getClaimsList(response);
  } catch (e) {
    getClaimsList({ isSuccess: false, errors: [COMMON_ERROR] });
  }
}

// Get grievance details for replying
function* submitReplyForGrievance({ payload: { replyGrievanceData, onSuccess, onError } }: any): Generator {
  const url = `${GLOBAL_URL.CRM_BASE_URL}Provider/Grievance/GetGrievanceDetails`;
  try {
    const grievanceResponse: any = yield SendRequest.instance.post(url, replyGrievanceData);
    const { isSuccess, errorMessage, grievanceTransactions, reason } = grievanceResponse || {};
    if (isSuccess) {
      onSuccess(grievanceTransactions, reason, grievanceResponse);
    } else {
      onError(errorMessage || COMMON_ERROR);
    }
  } catch (e) {
    onError(COMMON_ERROR);
  }
}

function* fetchGrievanceDetail({ payload: { id: ticketId, onSuccess, onError } }: any): Generator {
  const url = `${GLOBAL_URL.CRM_BASE_URL}Provider/Grievance/TicketDetails`;
  try {
    const { isSuccess, ticketList, errorMessage }: any = yield SendRequest.instance.post(url, { ticketId, includeConversations: true });
    if (isSuccess) {
      onSuccess(ticketList);
    } else {
      onError(errorMessage || COMMON_ERROR);
    }
  } catch (e) {
    onError(COMMON_ERROR);
  }
}

function* submitGrievanceReply({ payload: { data, onSuccess, onError } }: any): Generator {
  const files = data['files'];
  data = formatGrievanceDTO(data); // ! this line formats the user's input
  const url = `${GLOBAL_URL.CRM_BASE_URL}Provider/Grievance/TicketReply`;
  try {
    const { isSuccess, errorMessage }: any = yield SendRequest.instance.postFormData(url, data, (files || []));
    if (isSuccess) {
      onSuccess();
    } else {
      onError(errorMessage || COMMON_ERROR);
    }
  } catch (e) {
    console.error(e);
    onError(COMMON_ERROR);
  }
}

function* raiseNewSaga({ payload: { formData, onSuccess, onError } }: any): Generator {
  try {
    const files = formData['files'];
    const data = formatGrievanceDTO(formData);
    const url = GLOBAL_URL.CRM_BASE_URL + 'Provider/Grievance/CreateTicket';
    const { isSuccess, errorMessage }: any = yield SendRequest.instance.postFormData(url, data, (files || []));
    if (isSuccess) {
      onSuccess();
    } else {
      onError(errorMessage);
    }
  } catch (e) {
    onError(COMMON_ERROR);
  }
}

// For Raise or Update Grievance
function* raiseOrUpdateGrievanceSaga({ payload: { grievanceDetails, onSuccess, onError } }: any): Generator {
  try {
    const url = GLOBAL_URL.CRM_BASE_URL + 'Provider/Grievance/RaiseOrUpdateGrievance';
    const { isSuccess, errorMessage }: any = yield SendRequest.instance.post(url, grievanceDetails);
    if (isSuccess) {
      onSuccess();
    } else {
      onError(errorMessage || COMMON_ERROR);
    }
  } catch (e) {
    onError(COMMON_ERROR);
  }
}

function* closeTicket({ payload: { ticketId: id, onSuccess, onError } }: any): Generator {
  try {
    const url = GLOBAL_URL.CRM_BASE_URL + 'Provider/Grievance/TicketClose';
    const { isSuccess, errorMessage }: any = yield SendRequest.instance.post(url, { id, status: 5 });
    if (isSuccess) {
      onSuccess();
    } else {
      onError(errorMessage || COMMON_ERROR);
    }
  } catch (e) {
    onError(COMMON_ERROR);
  }
}

export function* watchGrievanceAction(): any {
  yield takeLatest(actionTypes.GET_GRIEVANCE_LIST, getGrievanceListSaga);
  yield takeLatest(actionTypes.SAVE_GRIEVANCE_RESPONSE, setGrievanceResponseSaga);
  yield takeLatest(actionTypes.FETCH_DETAILED_GRIEVANCE, fetchGrievanceDetail);
  yield takeLatest(actionTypes.FETCH_CLAIM_LIST, fetchClaimList);
  yield takeLatest(actionTypes.FETCH_GRIEVANCE_LIST, fetchGrievanceListSaga);
  yield takeLatest(actionTypes.SUBMIT_GRIEVANCE_REPLY, submitGrievanceReply);
  yield takeLatest(actionTypes.RAISE_NEW_GRIEVANCE, raiseNewSaga);
  yield takeLatest(actionTypes.CLOSE_TICKET, closeTicket);
  yield takeLatest(actionTypes.RAISE_OR_UPDATE_GRIEVANCE, raiseOrUpdateGrievanceSaga);
  yield takeLatest(actionTypes.REPLY_GRIEVANCE, submitReplyForGrievance);
}

export default watchGrievanceAction;
